import React from 'react';
import Picker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data';
import ChatMessage from '../chat-message';
import InlineChoiceButtons from './InlineChoiceButtons';
import InlineOtpInput from './InlineOtpInput';
import { getColorScheme } from '../theme/colorSchemes';
import {
  ChatAreaContainer, ChatTopBar, BotBadge, BotDot, BotName, TopActions, TopBtn,
  MessagesContainer, TypingRow, TypingBubble,
  InputArea, InputRow, InBtn, InField, PoweredRow,
  RecordingIconBtn, RecordingTimer, RecordingStatus, MicrophoneIcon,
  MessageAvatar,
} from '../ChatStyles';

const ChatArea = ({
  darkMode, scheme, messages, listRef, handleMessagesContainerClick, loadingMessage,
  emojiPickerOn, emojiSelected, openEmojiPicker,
  uploadRef, handleFileChange, uploadDoc,
  inputRef, currentMessage, handleInputChange, sendMessage,
  apikey, base_url,
  isRecording, recordingTime, toggleRecording, isTranscribing, transcriptionStatus, formatTime,
  questionnaireProgress,
  widgetPhase, selectedLanguage, identificationComplete,
  // `assessmentTemplates` is the server-resolved list for this adviser's discipline;
  // `handleChooseAssessment(id)` starts whichever one the visitor picks.
  assessmentTemplates, handleChooseAssessment, handleChooseIntake,
  showPoweredBy = false,
  showTimestamp = true,
  isMobile = false,
  closeChat,
  isIframe = false,
  feedbackState, onFeedbackThumb, onFeedbackSubmit, feedbackComment, setFeedbackComment,
  otpProps = null,
}) => {
  const colors = getColorScheme(scheme);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <ChatAreaContainer>
      {!isIframe && (
        <ChatTopBar>
          <BotBadge $scheme={scheme}>
            <BotDot $scheme={scheme}>✦</BotDot>
            <BotName $scheme={scheme}>AI Assistant</BotName>
          </BotBadge>
          <TopActions>
            {closeChat && (
              <TopBtn $scheme={scheme} title="Minimize" onClick={closeChat}>
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </TopBtn>
            )}
          </TopActions>
        </ChatTopBar>
      )}

      <MessagesContainer ref={listRef} onClick={handleMessagesContainerClick}>
        {messages.filter(msg => !msg.hideMessage).map((item, index) => {
          const visibleMessages = messages.filter(msg => !msg.hideMessage);
          const isLastMessage = index === visibleMessages.length - 1;

          if (item.type === 'choice_buttons') {
            return (
              <InlineChoiceButtons
                key={index}
                templates={assessmentTemplates}
                onChoose={handleChooseAssessment}
                onChooseIntake={handleChooseIntake}
                darkMode={darkMode}
                scheme={scheme}
                language={selectedLanguage}
              />
            );
          }

          if (item.type === 'otp_input') {
            return (
              <InlineOtpInput
                key={`otp-${index}`}
                maskedEmail={item.maskedEmail}
                darkMode={darkMode}
                language={selectedLanguage}
                error={otpProps?.error}
                submitting={otpProps?.submitting}
                onSubmit={(code) => otpProps?.onSubmit && otpProps.onSubmit(code)}
                onResend={() => otpProps?.onResend && otpProps.onResend()}
                onSkip={() => otpProps?.onSkip && otpProps.onSkip()}
              />
            );
          }

          if (item.type === 'booking_prompt') {
            const expertId = localStorage.getItem('imavenexpertid') || '';
            const convoId = localStorage.getItem('imavenconvoid') || '';
            const lang = selectedLanguage || 'en';
            const bookingUrl = `https://platform.imavenai.com/setMetting/${expertId}/${convoId}?lang=${lang}`;
            return (
              <div key={`booking-${index}`} style={{
                display: 'flex', justifyContent: 'center', padding: '12px 16px'
              }}>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '12px 28px',
                    borderRadius: 8,
                    background: scheme === 'dark' ? '#4CAF50' : '#2E7D32',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 15,
                    textDecoration: 'none',
                    textAlign: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: 'transform 0.15s ease'
                  }}
                >
                  {selectedLanguage === 'he' ? 'קבע פגישת ייעוץ' :
                   selectedLanguage === 'es' ? 'Reservar Consulta' :
                   selectedLanguage === 'fr' ? 'Réserver une consultation' :
                   selectedLanguage === 'ar' ? 'حجز استشارة' :
                   selectedLanguage === 'pt' ? 'Agendar Consulta' :
                   'Book a Consultation'}
                </a>
              </div>
            );
          }

          if (item.isFeedbackRequest) {
            return (
              <div key={`feedback-${index}`} style={{
                display: 'flex', justifyContent: 'center', padding: '8px 16px'
              }}>
                <div style={{
                  background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  borderRadius: 12, padding: '10px 16px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  maxWidth: 320
                }}>
                  {feedbackState === 'idle' && (
                    <>
                      <span style={{ fontSize: 13, color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}>
                        Was this conversation helpful?
                      </span>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button onClick={() => onFeedbackThumb('positive')} style={{
                          background: 'none', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                          borderRadius: 8, padding: '6px 16px', cursor: 'pointer',
                          fontSize: 18, transition: 'all 0.2s',
                          color: darkMode ? '#fff' : '#000'
                        }}>&#128077;</button>
                        <button onClick={() => onFeedbackThumb('negative')} style={{
                          background: 'none', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                          borderRadius: 8, padding: '6px 16px', cursor: 'pointer',
                          fontSize: 18, transition: 'all 0.2s',
                          color: darkMode ? '#fff' : '#000'
                        }}>&#128078;</button>
                      </div>
                    </>
                  )}
                  {feedbackState === 'positive' && (
                    <span style={{ fontSize: 13, color: '#22c55e' }}>Thanks for your feedback!</span>
                  )}
                  {feedbackState === 'commenting' && (
                    <>
                      <span style={{ fontSize: 13, color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}>
                        Sorry to hear that. What could be better?
                      </span>
                      <textarea
                        value={feedbackComment}
                        onChange={e => setFeedbackComment(e.target.value)}
                        placeholder="Optional: tell us more..."
                        style={{
                          width: '100%', minHeight: 60, background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                          border: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                          borderRadius: 8, color: darkMode ? '#fff' : '#000',
                          padding: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => onFeedbackSubmit()} style={{
                          background: scheme ? getColorScheme(scheme).primary : '#6366f1',
                          border: 'none', borderRadius: 8, color: '#fff',
                          padding: '6px 14px', cursor: 'pointer', fontSize: 12
                        }}>Submit</button>
                        <button onClick={() => onFeedbackSubmit(true)} style={{
                          background: 'none', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
                          borderRadius: 8, color: darkMode ? '#fff' : '#555',
                          padding: '6px 14px', cursor: 'pointer', fontSize: 12
                        }}>Skip</button>
                      </div>
                    </>
                  )}
                  {feedbackState === 'submitted' && (
                    <span style={{ fontSize: 13, color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)' }}>
                      Feedback recorded. Thank you!
                    </span>
                  )}
                </div>
              </div>
            );
          }

          return (
            <ChatMessage
              key={index}
              message={item.message}
              type={item.type}
              apikey={apikey}
              darkMode={darkMode}
              scheme={scheme}
              loading={loadingMessage && isLastMessage}
              timestamp={showTimestamp ? item.timestamp : undefined}
            />
          );
        })}

        {loadingMessage && messages.filter(msg => !msg.hideMessage).length === 0 && (
          <TypingRow>
            <MessageAvatar $type="gpt" $scheme={scheme}>✦</MessageAvatar>
            <TypingBubble><span /><span /><span /></TypingBubble>
          </TypingRow>
        )}
      </MessagesContainer>

      {!(identificationComplete && widgetPhase === 'identification') && (
        <div style={{ position: 'relative' }}>
          {emojiPickerOn && (
            <div style={{ position: 'absolute', bottom: '100%', left: isMobile ? 0 : 8, zIndex: 100 }}
              className="emoji-picker-wrapper">
              <Picker data={emojiData} onEmojiSelect={emojiSelected} theme="light" />
            </div>
          )}

          <InputArea $isMobile={isMobile}>
            <InputRow $scheme={scheme} $isMobile={isMobile}>
              <input name="files" type="file" style={{ display: 'none' }}
                ref={uploadRef}
                accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.html,.md,.csv,.json,.epub"
                multiple onChange={handleFileChange} />

              <InBtn $scheme={scheme} $isMobile={isMobile} disabled={loadingMessage} onClick={uploadDoc} title="Attach">
                <svg width={isMobile ? 14 : 15} height={isMobile ? 14 : 15} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
              </InBtn>

              {!isMobile && (
                <InBtn $scheme={scheme} onClick={openEmojiPicker} title="Emoji">
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
                  </svg>
                </InBtn>
              )}

              <InField
                as="textarea"
                ref={inputRef}
                $isMobile={isMobile}
                placeholder="Type a message..."
                value={currentMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={loadingMessage}
                rows={1}
              />

              {currentMessage.trim() ? (
                <InBtn $scheme={scheme} $isMobile={isMobile} $send onClick={sendMessage} disabled={loadingMessage} title="Send">
                  <svg width={isMobile ? 14 : 15} height={isMobile ? 14 : 15} fill="none" viewBox="0 0 24 24">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </InBtn>
              ) : isRecording ? (
                <div style={{ position: 'relative' }}>
                  <RecordingIconBtn $scheme={scheme} $isMobile={isMobile} onClick={toggleRecording} title="Stop recording">
                    <svg width={isMobile ? 14 : 15} height={isMobile ? 14 : 15} fill="none" stroke="#e53e3e" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                      <path d="M19 10v2a7 7 0 01-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" />
                    </svg>
                  </RecordingIconBtn>
                  <RecordingTimer $scheme={scheme}>{formatTime(recordingTime)}</RecordingTimer>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <InBtn $scheme={scheme} $isMobile={isMobile} onClick={toggleRecording}
                    disabled={loadingMessage || isTranscribing} title="Voice">
                    <MicrophoneIcon color={colors.primary} size={isMobile ? 16 : 18} />
                  </InBtn>
                  {isTranscribing && <RecordingStatus $scheme={scheme}>{transcriptionStatus}</RecordingStatus>}
                </div>
              )}
            </InputRow>

            {showPoweredBy && (
              <PoweredRow $isMobile={isMobile}>
                <a href="https://imavenai.com/" target="_blank" rel="noopener noreferrer">
                  Powered by <strong>iMaven</strong>
                </a>
              </PoweredRow>
            )}
          </InputArea>
        </div>
      )}
    </ChatAreaContainer>
  );
};

export default ChatArea;
